import { apiGet } from './api-client';

/** Raw TfNSW Trip Planner response types */
interface TfNSWLeg {
  origin: { name: string; id: string; departureTimeEstimated?: string; departureTimePlanned?: string; coord?: [number, number] };
  destination: { name: string; id: string; arrivalTimeEstimated?: string; arrivalTimePlanned?: string; coord?: [number, number] };
  transportation: {
    product: { name: string; class?: number; iconId?: number };
    number?: string;
    destination?: { name: string };
    properties?: { TRIP_CODE?: string; REAL_TIME?: string };
  };
  duration: number;
  distance?: number;
  footPathInfo?: { duration: number; distance: number };
  coords?: [number, number][] | string;
  stopSequence?: { name: string; id: string; departureTimePlanned?: string; arrivalTimePlanned?: string; coord?: [number, number] }[];
}

interface TfNSWJourney {
  legs: TfNSWLeg[];
  interchanges: number;
  duration: number;
  departure: string;
  arrival: string;
}

interface TfNSWTripResponse {
  journeys: TfNSWJourney[];
}

/** Clean journey result */
export interface JourneyLeg {
  origin: { name: string; id: string; departure: string; coords?: [number, number] };
  destination: { name: string; id: string; arrival: string; coords?: [number, number] };
  mode: string;
  modeName: string;
  duration: number;
  distance?: number;
  headsign?: string;
  routeShortName?: string;
  path?: [number, number][];
  isRealtime?: boolean;
  platform?: string;
  stops?: { name: string; id: string; departure?: string; arrival?: string; coords?: [number, number] }[];
}

export interface Journey {
  legs: JourneyLeg[];
  transfers: number;
  duration: number;
  departure: string;
  arrival: string;
}

const MODE_NAMES: Record<number, string> = {
  1: 'Train', 2: 'Train', 3: 'Train', 4: 'Light Rail',
  5: 'Bus', 6: 'Bus', 7: 'Bus', 8: 'Train',
  9: 'Ferry',
  10: 'Bus',
  11: 'Bus', 12: 'Bus',
};

function transitModeName(leg: TfNSWLeg): string {
  const product = leg.transportation?.product?.name?.toLocaleLowerCase() || '';
  if (product.includes('light rail')) return 'Light Rail';
  if (product.includes('metro')) return 'Metro';
  if (product.includes('ferr')) return 'Ferry';
  if (product.includes('bus')) return 'Bus';
  if (product.includes('train') || product.includes('rail')) return 'Train';
  return MODE_NAMES[leg.transportation.product.class ?? 0] || 'Transit';
}

function isWalkLeg(leg: TfNSWLeg): boolean {
  const product = leg.transportation?.product?.name?.toLocaleLowerCase() || '';
  return !leg.transportation?.number && (Boolean(leg.footPathInfo) || product.includes('walk') || product.includes('foot'));
}

export interface PlanTripOptions {
  originType?: 'stop' | 'any';
  destinationType?: 'stop' | 'any';
  routeShortName?: string;
  resultCount?: number;
  addAccessLeg?: boolean;
}

function boardingLocationName(name: string): string {
  return name.replace(/,\s*(Platform|Stand)\s+[^,]+(?=,|$).*/i, '').trim();
}

function normalizeCoordinateList(value: TfNSWLeg['coords']): [number, number][] | undefined {
  if (Array.isArray(value)) {
    const points = value.filter(point => Array.isArray(point) && point.length >= 2)
      .map(point => [Number(point[0]), Number(point[1])] as [number, number])
      .filter(point => Number.isFinite(point[0]) && Number.isFinite(point[1]));
    return points.length >= 2 ? points : undefined;
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return normalizeCoordinateList(parsed);
    } catch {
      const points = value.split(/[;|]/).map(pair => pair.trim().split(/[,\s]+/).map(Number))
        .filter(pair => pair.length >= 2 && pair.every(Number.isFinite))
        .map(pair => [pair[0], pair[1]] as [number, number]);
      return points.length >= 2 ? points : undefined;
    }
  }
  return undefined;
}

export async function planTrip(
  originId: string,
  destId: string,
  time?: string,
  isDeparture = true,
  signal?: AbortSignal,
  options: PlanTripOptions = {},
): Promise<Journey[]> {
  // First try with type='stop' (strict transit stop lookup)
  const params = {
    type_origin: options.originType || 'stop',
    name_origin: originId,
    type_destination: options.destinationType || 'stop',
    name_destination: destId,
    ...(time ? { itdDate: time.split('T')[0].replace(/-/g, '') } : {}),
    ...(time ? { itdTime: (time.split('T')[1] || '').slice(0, 5).replace(/:/g, '') } : {}),
    itdTripDateTimeDepArr: isDeparture ? 'dep' : 'arr',
    outputFormat: 'rapidJSON',
    coordOutputFormat: 'EPSG:4326',
    coordListOutputFormat: 'JSON',
    ...(options.resultCount ? { calcNumberOfTrips: String(options.resultCount) } : {}),
    ...(options.routeShortName ? { lineRestriction: options.routeShortName, lineRestrictionType: '400' } : {}),
  };

  const data = await apiGet<TfNSWTripResponse>('/tp/trip', { params, signal });

  if (!data?.journeys) return [];

  const journeys = data.journeys.map((j) => {
    const firstLeg = j.legs[0];
    const lastLeg = j.legs[j.legs.length - 1];
    const departure = firstLeg?.origin?.departureTimeEstimated || firstLeg?.origin?.departureTimePlanned || '';
    const arrival = lastLeg?.destination?.arrivalTimeEstimated || lastLeg?.destination?.arrivalTimePlanned || '';
    const totalDuration = j.legs.reduce((sum, l) => sum + (l.duration || 0), 0);
    const legs: JourneyLeg[] = j.legs.map((leg) => ({
        origin: {
          name: leg.origin.name,
          id: leg.origin.id,
          departure: leg.origin.departureTimeEstimated || leg.origin.departureTimePlanned || '',
          coords: leg.origin.coord ? [leg.origin.coord[1], leg.origin.coord[0]] : undefined,
        },
        destination: {
          name: leg.destination.name,
          id: leg.destination.id,
          arrival: leg.destination.arrivalTimeEstimated || leg.destination.arrivalTimePlanned || '',
          coords: leg.destination.coord ? [leg.destination.coord[1], leg.destination.coord[0]] : undefined,
        },
        mode: isWalkLeg(leg) ? 'walk' : 'transit',
        modeName: isWalkLeg(leg)
          ? 'Walk'
          : transitModeName(leg),
        duration: leg.duration,
        distance: leg.distance || leg.footPathInfo?.distance,
        headsign: leg.transportation.destination?.name,
        routeShortName: leg.transportation.number,
        path: normalizeCoordinateList(leg.coords)?.map(c => [c[1], c[0]] as [number, number]),
        isRealtime: leg.transportation.properties?.REAL_TIME === '1',
        platform: leg.origin.id === leg.destination.id ? undefined : (leg as any).origin?.platform?.name,
        stops: leg.stopSequence?.map((s) => ({
          name: s.name,
          id: s.id,
          departure: s.departureTimePlanned,
          arrival: s.arrivalTimePlanned,
          coords: s.coord ? [s.coord[1], s.coord[0]] : undefined,
        })),
      }));
    if (options.addAccessLeg !== false && legs[0]?.mode !== 'walk' && legs[0]?.origin.id !== originId) {
      const first = legs[0];
      const stationName = boardingLocationName(first.origin.name);
      legs.unshift({
        origin: { name: stationName, id: originId, departure: first.origin.departure, coords: first.origin.coords },
        destination: { name: stationName, id: first.origin.id, arrival: first.origin.departure, coords: first.origin.coords },
        mode: 'walk', modeName: 'Walk', duration: 60, distance: 0,
      });
    }
    return {
      legs,
      transfers: j.interchanges,
      duration: totalDuration,
      departure,
      arrival,
    };
  });

  // If ALL journeys are walk-only, retry with type='any' (the stop IDs may be POIs)
  if (journeys.length > 0 && journeys.every(j => j.legs.every(l => l.mode === 'walk'))) {
    console.warn('planTrip: all journeys walk-only with type=stop, retrying with type=any', { originId, destId });
    try {
      const retryParams = { ...params, type_origin: 'any' as const, type_destination: 'any' as const };
      const retryData = await apiGet<TfNSWTripResponse>('/tp/trip', { params: retryParams, signal });
      if (retryData?.journeys) {
        const retryJourneys = retryData.journeys.map((j) => {
          const firstLeg = j.legs[0];
          const lastLeg = j.legs[j.legs.length - 1];
          const departure = firstLeg?.origin?.departureTimeEstimated || firstLeg?.origin?.departureTimePlanned || '';
          const arrival = lastLeg?.destination?.arrivalTimeEstimated || lastLeg?.destination?.arrivalTimePlanned || '';
          const totalDuration = j.legs.reduce((sum, l) => sum + (l.duration || 0), 0);
          return {
            legs: j.legs.map((leg) => ({
              origin: {
                name: leg.origin.name, id: leg.origin.id,
                departure: leg.origin.departureTimeEstimated || leg.origin.departureTimePlanned || '',
                coords: leg.origin.coord ? [leg.origin.coord[1], leg.origin.coord[0]] as [number, number] : undefined,
              },
              destination: {
                name: leg.destination.name, id: leg.destination.id,
                arrival: leg.destination.arrivalTimeEstimated || leg.destination.arrivalTimePlanned || '',
                coords: leg.destination.coord ? [leg.destination.coord[1], leg.destination.coord[0]] as [number, number] : undefined,
              },
              mode: isWalkLeg(leg) ? 'walk' : 'transit',
              modeName: isWalkLeg(leg) ? 'Walk' : transitModeName(leg),
              duration: leg.duration, distance: leg.distance || leg.footPathInfo?.distance,
              headsign: leg.transportation.destination?.name,
              routeShortName: leg.transportation.number,
              path: normalizeCoordinateList(leg.coords)?.map(c => [c[1], c[0]] as [number, number]),
              isRealtime: leg.transportation.properties?.REAL_TIME === '1',
              platform: leg.origin.id === leg.destination.id ? undefined : (leg as any).origin?.platform?.name,
              stops: leg.stopSequence?.map((s) => ({
                name: s.name, id: s.id, departure: s.departureTimePlanned, arrival: s.arrivalTimePlanned,
                coords: s.coord ? [s.coord[1], s.coord[0]] as [number, number] : undefined,
              })),
            })),
            transfers: j.interchanges, duration: totalDuration, departure, arrival,
          };
        });
        if (retryJourneys.length > 0 && !retryJourneys.every(j => j.legs.every(l => l.mode === 'walk'))) {
          return retryJourneys as Journey[];
        }
      }
    } catch { /* retry failed — return original walk-only results */ }
  }

  return journeys as Journey[];
}
